import { t } from '@lingui/core/macro';
import * as React from 'react';
import { carReady, endSession, releaseVoipMasterOnClose, syncSnapshot, userSession } from 'state/session';
import { myCameraStream } from 'state/video';
import { voipRtcConnection } from 'utils/ui/rtc';
import { hasSameDealerPeer } from 'utils/ui/voipRemotes';
import { user } from 'utils/auth/user';
import { IShowroomProps } from 'components/Showroom';
import { getCurrentUrlParameter } from 'utils/url';

const useUnloadEvents = (userType: IShowroomProps['userType']) => {
  const isReloading = React.useRef(false);

  React.useEffect(() => {
    if (userType === 'dealer') {
      registerUnloadEvents();
    }
    return () => {
      cleanupUnloadEvents();
    };
    // eslint-disable-next-line
  }, []);

  /**
   * These are PROPERTY assignments, and two things depend on them staying that way (AVS-4427).
   *
   * 1. Anything else that wants `pagehide` must use `addEventListener` — an assignment there would
   *    delete `handleUnload` and the session would never end server-side. The Session Flight
   *    Recorder's transport registers its unload tail that way for exactly this reason
   *    (`telemetry/recorder/transport.ts`, pinned by `unloadKeepalive.test.ts`).
   * 2. `handleUnload` fires `endSession({ keepAlive: true })`, whose full-body PUT shares the Fetch
   *    spec's 64 KiB **per-document** keepalive budget with every other keepalive request on the
   *    page. Anything new that posts at unload has to fit inside what is left, or this PUT is the
   *    one the browser refuses.
   */
  function registerUnloadEvents() {
    document.onkeydown = handleKeyDown;
    window.onload = handleLoad;
    window.onbeforeunload = handleBeforeUnload;
    window.onpagehide = handleUnload;
  }

  function cleanupUnloadEvents() {
    document.onkeydown = null;
    window.onload = null;
    window.onbeforeunload = null;
    window.onpagehide = null;
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'F5' || (event.ctrlKey && event.key === 'r') || (event.metaKey && event.key === 'r')) {
      isReloading.current = true;
    }
  }

  function handleLoad() {
    isReloading.current = false;
  }

  function handleBeforeUnload(event: BeforeUnloadEvent) {
    const session = userSession();
    // Show warning if session exists and a car is loaded (has prString)
    if (isReloading.current || !session.get('id') || !carReady()) {
      return;
    }
    const confirmationMessage = t`Are you sure you want to leave this page?`;
    event.preventDefault();
    Object.defineProperty(event, 'returnValue', {
      value: confirmationMessage,
    });
    return confirmationMessage;
  }

  function handleUnload() {
    if (isReloading.current) {
      syncSnapshot();
      return;
    }
    // Allow cleanup in production OR when testing with ?testUnload=true URL parameter
    if (import.meta.env.MODE !== 'development' || getCurrentUrlParameter('testUnload')) {
      // AVS-4302 (§5.9): decide whether this close should keep the session alive for a
      // surviving same-dealer tab to "Take over". This MUST read the RAW VOIP store
      // BEFORE destroy() — destroy() resets the store to defaults (remoteConnections: {}),
      // so reading afterwards would always look "empty" and wrongly keep every session alive.
      const keepAliveForTakeover = shouldKeepSessionAliveForTakeover();
      // AVS-4485: hand the master and 3D host roles to a survivor. Reads the peer list, so before destroy().
      if (keepAliveForTakeover) {
        releaseVoipMasterOnClose();
      }

      // ensure proper cleanup and media allocation release
      myCameraStream()?.destroy();
      voipRtcConnection?.destroy();

      // don't end session when developing because it's uber frustrating
      if (!keepAliveForTakeover) {
        // No surviving same-dealer tab to take over (or the feature is off):
        // end as today so a normal single-tab close terminates promptly (AC f).
        endSession({ keepAlive: true });
      }
      // else: the destroys above already released media and disconnected this tab;
      // the session stays alive so another same-dealer tab can take over.
    }
  }

  /**
   * AVS-4302 (§5.9): true iff closing this dealer tab should leave the session running
   * for a surviving same-dealer tab to take over. Reads the RAW `voipRtcConnection.store`
   * `remoteConnections` (NOT a deduped/hook-derived list) and MUST be called BEFORE any
   * destroy() that clears the store.
   *
   * Rules:
   * - Feature off ⇒ always end (today's behaviour; minimal regression).
   * - A remote with `meta.dealerPseudonym === this dealer` ⇒ a surviving same-dealer tab
   *   exists ⇒ keep alive.
   * - An EMPTY store ("unknown" — transiently cleared during a WsClientReconnected blip)
   *   ⇒ keep alive ONLY when this tab is a standby (another same-dealer tab owns the
   *   master slot, so a survivor definitely exists). A master with a legitimately empty
   *   store (no peers connected yet) must end: otherwise closing the only tab orphans the
   *   session forever.
   * - A non-empty store with NO same-dealer remote ⇒ a customer/other peer is present but
   *   no survivor ⇒ end as today (the realistic normal single-tab close — AC f).
   */
  function shouldKeepSessionAliveForTakeover(): boolean {
    if (!userSession().isVoipSingleStreamEnabled) {
      return false;
    }

    const remoteConnections = voipRtcConnection?.store.get('remoteConnections') ?? {};
    const remotes = Object.values(remoteConnections);

    const remoteStoreEmpty = remotes.length === 0;
    const hasSurvivingSameDealerTab = hasSameDealerPeer(remotes, user.userPseudonym);

    return hasSurvivingSameDealerTab || (remoteStoreEmpty && userSession().isVoipStandby);
  }
};

export default useUnloadEvents;
